/**
 * Utility functions for grouping and organizing transactions
 */

export interface AccountGroup {
  accountId: string;
  accountName: string;
  transactions: any[];
  summary: {
    totalIn: number;
    totalOut: number;
    netAmount: number;
    transactionCount: number;
  };
}

/**
 * Group transactions by account
 * @param transactions Array of transactions with accountId and accountName
 * @returns Record of account groups indexed by accountId
 */
export function groupTransactionsByAccount(transactions: any[]): Record<string, AccountGroup> {
  if (!transactions || transactions.length === 0) {
    return {};
  }

  return transactions.reduce((acc, transaction) => {
    // Use accountId or fallback to a default key for transactions without an account
    const accountId = transaction.accountId || 'unknown';
    const accountName = transaction.accountName || 'Unknown Account';

    if (!acc[accountId]) {
      acc[accountId] = {
        accountId,
        accountName,
        transactions: [],
        summary: {
          totalIn: 0,
          totalOut: 0,
          netAmount: 0,
          transactionCount: 0,
        },
      };
    }

    acc[accountId].transactions.push(transaction);
    acc[accountId].summary.totalIn += transaction.fundsIn || 0;
    acc[accountId].summary.totalOut += transaction.fundsOut || 0;
    acc[accountId].summary.netAmount += (transaction.fundsIn || 0) - (transaction.fundsOut || 0);
    acc[accountId].summary.transactionCount += 1;

    return acc;
  }, {} as Record<string, AccountGroup>);
}

/**
 * Convert account groups record to sorted array
 * Sorts by account name alphabetically
 */
export function accountGroupsToArray(groups: Record<string, AccountGroup>): AccountGroup[] {
  return Object.values(groups).sort((a, b) => a.accountName.localeCompare(b.accountName));
}



